import api from './api';
import { User, PaginatedResponse, UserStatsSummary } from '../types';

export interface CreateUserRequest {
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  role: 'A' | 'S' | 'O' | 'M' | 'W' | 'R' | 'admin' | 'supervisor' | 'operator';
  isActive: boolean;
  employee_number?: string;
}

export interface AvailableEmployee {
  employee_number: string;
  display_name: string;
}

export interface UpdateUserRequest extends Partial<CreateUserRequest> {}

export type { User };

export const userService = {
  async getUsers(params?: { page?: number; limit?: number; search?: string; role?: string }): Promise<PaginatedResponse<User>> {
    const response = await api.get('/users', { params });
    return response.data.data;
  },

  async getUser(id: string): Promise<User> {
    const response = await api.get(`/users/${id}`);
    return response.data.data;
  },

  async createUser(userData: CreateUserRequest): Promise<User> {
    const response = await api.post('/users', userData);
    return response.data.data;
  },

  async updateUser(id: string, userData: UpdateUserRequest): Promise<User> {
    const response = await api.put(`/users/${id}`, userData);
    return response.data.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async toggleUserStatus(id: string): Promise<User> {
    const response = await api.patch(`/users/${id}/toggle-status`);
    return response.data.data;
  },

  async resetPassword(id: string, newPassword: string): Promise<void> {
    await api.patch(`/users/${id}/reset-password`, { password: newPassword });
  },

  async validateUsername(username: string): Promise<{ available: boolean }> {
    const response = await api.get(`/users/validate/username/${username}`);
    return response.data.data;
  },

  async validateEmail(email: string): Promise<{ available: boolean }> {
    const response = await api.get(`/users/validate/email/${encodeURIComponent(email)}`);
    return response.data.data;
  },

  async getUserStats(): Promise<UserStatsSummary> {
    const response = await api.get('/users/stats/summary');
    return response.data.data;
  },

  async getRecentActivity(limit: number = 10): Promise<any[]> {
    const response = await api.get('/users/stats/activity', { params: { limit } });
    return response.data.data;
  },

  async getAvailableEmployees(search?: string): Promise<AvailableEmployee[]> {
    const response = await api.get('/users/available-employees', { params: search ? { search } : {} });
    return response.data.data.employees;
  },
};

export default userService;